import { Component, ElementRef, HostListener, Input, ViewChild, inject } from '@angular/core';
import { CommonModule, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { Constants } from '../../../core/const/constants';
import { HelperFunctionService } from '../../../core/services/helper-function.service';
import { Router } from '@angular/router';
import { BroadcastChannelService } from '../../../core/services/cross-tab-authentication.service';
import { OrgAppInfoHelperService } from '../../../core/services/org-app-info-helper.service';
import { firstValueFrom } from 'rxjs';
import { AppIconUrlTransformPipe } from '../../../core/pipes/appicon-url-transform.pipe';
import { TabSdk } from '@techextensor/tab-sdk';
import { PermissionService } from '../../../core/services/permission.service';
import { environment } from '../../../../environments/environment';
import { StorageConstants } from '@techextensor/tab-core-utility';
import { DevToolBridgeService } from '../../../core/services/dev-tool/dev-tool-bridge.service';
import { ExternalAppsService, ExternalAppTile } from '../../../core/services/external-apps.service';
import { EXTERNAL_APP_CODE_PREFIX } from '../../../core/const/constants';

/** Tile geometry. Kept here and in the stylesheet in step - see `tileRowWidth`. */
const TILE_MAX_PX = 110;
const TILE_GAP_PX = 2;
const TILES_PADDING_PX = 20;

@Component({
    selector: 'app-switcher',
    imports: [CommonModule, AppIconUrlTransformPipe, NgIf, FormsModule],
    templateUrl: './tab-app-switch.component.html',
    styleUrl: './tab-app-switch.component.scss'
})
export class AppSwtichComponent {
  private readonly router: Router = inject(Router);
  private readonly _permissionService: PermissionService = inject(PermissionService);
  private readonly _helperFunctionService: HelperFunctionService = inject(HelperFunctionService);
  private readonly _broadcastChannelService: BroadcastChannelService = inject(BroadcastChannelService);
  private readonly _orgAppInfoHelper: OrgAppInfoHelperService = inject(OrgAppInfoHelperService);
  private readonly _devToolBridge: DevToolBridgeService = inject(DevToolBridgeService);
  private readonly _externalAppsService: ExternalAppsService = inject(ExternalAppsService);

  @ViewChild('popup') popup: ElementRef | undefined;
  selectedTenant: any;
  selectedAppId: any;
  isPopupVisible?: boolean;
  isLoading?: boolean;
  /** Initialised, because the template indexes appList[0] and an empty list is now reachable. */
  appList: any[] = [];
  isAppListVisible?: boolean;
  tenantList: any[] = [];

  /**
   * Which tenant the left rail is showing. Tracked separately from selectedTenant, which means "the
   * tenant I am working in" - clicking the rail used to change the list without recording the choice,
   * so anything keyed off it looked at the wrong tenant.
   */
  railTenant: any;

  /** Apps this person has that open outside TAF. Their own list, with their own click handler. */
  externalApps: ExternalAppTile[] = [];

  /** Free-text filter across both lists. At sixty tiles a flat grid stops being usable. */
  searchTerm = '';
  canShowCreateAppOption = false;
  @Input() isAdminRoute: boolean = false;

  ngOnInit(): void {
    this.selectedTenant = TabSdk.store.tenantId;
    this.selectedAppId = TabSdk.store.appId;
    this.getTenantListfromLoggedInUser();
    this.canShowCreateAppOption = (
      this._permissionService.doesUserIsTabAdministrator() || this._permissionService.doesUserIsTenantAdministrator()
    ) && !this.isAdminRoute;
  }

  getTenantListfromLoggedInUser() {
    // Show loader before making the API call
    this.isLoading = true;

    // Execute the API call
    this._helperFunctionService
      .getDSQData(Constants.Get_Tenants_TABD_Tenant_Users, { Id: TabSdk.store.userId })
      .subscribe({
        // The loader and the app fetch run whatever comes back. They used to sit inside
        // `if (res.Result.length > 0)`, so an empty or failed response left the panel spinning for
        // ever - which a person whose tiles are mostly external now hits routinely.
        next: (res: any) => {
          this.tenantList = res?.Result ?? [];
          this.railTenant = this.selectedTenant;
          this.getAppListByTenant(this.selectedTenant);
        },
        error: () => {
          this.tenantList = [];
          this.isLoading = false;
        }
      });
  }

  // Tenant shortener
  getInitials(phrase: string): string {
    return phrase
      .split(' ') // Split the phrase into words
      .map(word => word[0]) // Get the first letter of each word
      .join(''); // Join them into a single string
  }
  /**
   * Sets the selected organization and makes an API call to fetch tenant data.
   *
   * @param tenant - The selected tenant object
   */
  getAppListByTenant(tenant: any) {
    // Show loader before making the API call
    this.isLoading = true;
    this.railTenant = tenant;

    // Execute the API call to fetch tenant data
    this._helperFunctionService
      .getDSQData(Constants.Get_AppList_TenantAppUserSubscriptionsView, { TenantId: tenant })
      .subscribe({
        next: (res: any) => {
          // External apps are rows in this view too, so they are removed here and fetched properly
          // below. Without this they would render as ordinary tiles, and clicking one would run the
          // TAF session hand-off against something that is not a TAF app.
          this.appList = (res?.Result ?? []).filter((app: any) => !this.isExternalRow(app));
          this.loadExternalApps(tenant);
        },
        error: () => {
          this.appList = [];
          this.loadExternalApps(tenant);
        }
      });
  }

  /**
   * Whether a row from the app-list query describes an app that opens outside TAF.
   *
   * The query now selects IsExternal, so that column is the answer when it is there. It is not
   * always there: SelectedFields is environment metadata, so an environment whose metadata has not
   * been updated yet returns rows without the column, and `undefined` would read as "not external".
   * That is the dangerous direction to be wrong in - an external app rendered as an ordinary tile
   * hands the TAF session to a site that is not TAF - so the app-code prefix remains as the
   * fallback rather than being deleted along with the reason for it.
   *
   * Drop the fallback only once every environment's metadata carries the column.
   */
  private isExternalRow(app: any): boolean {
    const flag = app?.IsExternal;
    if (flag !== undefined && flag !== null && flag !== '') {
      return flag === true || String(flag).toLowerCase() === 'true';
    }
    return String(app?.AppName ?? '').toLowerCase().startsWith(EXTERNAL_APP_CODE_PREFIX);
  }

  /**
   * The same person's apps that open outside TAF, from the Identity service. Never rejects, so a
   * failure here still leaves the TAF tiles usable.
   */
  private loadExternalApps(tenantId: any) {
    this._externalAppsService
      .listForUser(String(tenantId))
      .then(tiles => { this.externalApps = tiles; })
      .catch(() => { this.externalApps = []; })
      .finally(() => {
        this.showAppSwitcher();
        this.isLoading = false;
      });
  }

  /** TAF apps matching the search box. */
  get filteredAppList(): any[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) { return this.appList; }
    return this.appList.filter((app: any) => String(app?.AppName ?? '').toLowerCase().includes(term));
  }

  /** External apps matching the search box, in the order the server sent them. */
  get filteredExternalApps(): ExternalAppTile[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) { return this.externalApps; }
    return this.externalApps.filter(
      app => app.name.toLowerCase().includes(term) || (app.host ?? '').toLowerCase().includes(term)
    );
  }

  /** The search box only earns its space once there are enough tiles to lose one in. */
  get isSearchVisible(): boolean {
    return (this.appList.length + this.externalApps.length) > 8;
  }

  /**
   * Whether this person belongs to more than one organisation. Measured on dev, 97.6% do not, and
   * the launcher used to spend 68px on an always-present organisation rail for all of them.
   */
  get hasMultipleTenants(): boolean {
    return this.tenantList.length > 1;
  }

  /**
   * The rail label. `getInitials` takes the first letter of every word, so a four-word
   * organisation name produced four characters in a 34px square, and it throws outright on a
   * missing name. Two letters, and never a crash.
   */
  orgInitials(name: string | null | undefined): string {
    const words = String(name ?? '').trim().split(/\s+/).filter(w => w.length);
    if (!words.length) { return '?'; }
    const letters = words.length > 1
      ? words[0][0] + words[1][0]
      : words[0].slice(0, 2);
    return letters.toUpperCase();
  }

  /**
   * The widest a tile row is allowed to get, which is what decides the column count.
   *
   * The grid itself is `repeat(auto-fill, minmax(74px, 110px))`, so it fits as many columns as
   * the space allows and drops a column by itself on a narrow screen. This cap is the only thing
   * stopping it going past three: one or two apps get one or two columns rather than two tiles
   * stranded in a three-column grid, anything more fills three, and nothing is ever wider than
   * the phone it is on. Setting the template here instead would have fixed the count and made the
   * panel overflow below about 360px.
   */
  get tileRowWidth(): number {
    const total = this.filteredAppList.length + this.filteredExternalApps.length;
    const columns = total <= 2 ? Math.max(1, total) : 3;
    return (columns * TILE_MAX_PX) + ((columns - 1) * TILE_GAP_PX) + TILES_PADDING_PX;
  }

  /** First letter of an app's name, for a tile with no icon of its own. */
  monogram(name: string | null | undefined): string {
    return (String(name ?? '').trim()[0] ?? '?').toUpperCase();
  }

  /**
   * A stable colour per name, so two apps without icons are told apart at a glance. They all shared
   * one generic picture before, which made "no icon" and "broken icon" look identical.
   */
  monogramColour(name: string | null | undefined): string {
    const text = String(name ?? '');
    let hash = 0;
    for (const character of text) {
      hash = (hash * 31 + character.charCodeAt(0)) % 360;
    }
    return `hsl(${hash} 55% 47%)`;
  }

  /** The organisation name shown above the tiles, from the rail rather than from appList[0]. */
  get railTenantName(): string {
    const row = this.tenantList.find(
      (t: any) => t?.TenantId?.Id?.toLowerCase() === String(this.railTenant ?? '').toLowerCase()
    );
    return row?.TenantId?.OrganizationName ?? '';
  }

  /**
   * Opens an external app. Deliberately a separate handler from selectedApp: that one packs the TAF
   * session into the address it navigates to, which is right between TAF origins and nowhere else.
   * Two handlers make that leak impossible to write rather than merely unlikely.
   */
  openExternalApp(app: ExternalAppTile, event?: Event) {
    event?.preventDefault();
    this._externalAppsService.open(app);
    this.isPopupVisible = false;
  }
  async selectedApp(appDetail: any, tenatId: string) {
    this._devToolBridge.closeDevTools();
    const currentTenant = TabSdk.store.tenantId;
    const authData = this._broadcastChannelService.getAuthData();
    const transferData = { authData, appCode: appDetail.AppName };

    if (currentTenant?.toLowerCase() === appDetail?.TenantId?.Id?.toLowerCase()) {
      this._orgAppInfoHelper.setAppInfo({
        ...appDetail,
        Id: appDetail.AppId,
      });

      this.router.navigate(['initializing'], {
        fragment: this._helperFunctionService.getAuthTransferFragment(transferData),
        replaceUrl: true
      });

    } else {
      const { subdomain, domain } = this._orgAppInfoHelper.extractDomainInfo();

      if (this._orgAppInfoHelper.isValidDomain(domain)) {
        const newSubdomain = appDetail?.TenantId?.Subdomain;
        window.location.replace(this._helperFunctionService.buildAuthTransferUrl(`https://${newSubdomain}.${domain}/initializing`, transferData));

      } else {
        const orgInfoRes: any = await firstValueFrom(this._orgAppInfoHelper.getOrgInfoApiObservable({ Id: appDetail.TenantId.Id }));
        const data = orgInfoRes?.Result?.[0];
        if (data && typeof data?.LocaleSettings === 'string') {
          data.LocaleSettings = JSON.parse(data.LocaleSettings)
        }

        TabSdk.store.setOrgInfo(data);

        this._orgAppInfoHelper.setAppInfo({
          ...appDetail,
          Id: appDetail.AppId,
        });

        window.location.replace(this._helperFunctionService.buildAuthTransferUrl('initializing', transferData));
      }
    }

  }

  showAppSwitcher() {
    // Counts BOTH lists. Counting only TAF apps hid the launcher button entirely from anyone with one
    // TAF app and any number of external tiles - the common case this feature creates.
    const tileCount = (this.appList?.length ?? 0) + (this.externalApps?.length ?? 0);
    this.isAppListVisible = this.tenantList?.length > 1 || (this.tenantList?.length === 1 && tileCount > 1);
  }
  toggleContentVisibility() {
    // Toggle the visibility of the popup content
    this.isPopupVisible = !this.isPopupVisible;
  }
  @HostListener('document:mousedown', ['$event'])
  public onOutsideClick(event: any): void {
    if (this.isPopupVisible && this.popup && !this.popup.nativeElement.contains(event.target)) {
      this.isPopupVisible = false;
    }
  }
  addApp() {
    const appInfo = TabSdk.store.getAppInfo() ?? {};
    const appEnv = TabSdk.store.environmentId;
    TabSdk.store.setSession(StorageConstants.appNavigationMapping, {appInfo, appEnv});
    
    const appDetail = {
      AppName: environment.commonConfig.applicationCode,
      DisplayName: environment.commonConfig.applicationCode,
      Id: environment.commonConfig.applicationId,
      TenantId: { Id: environment.commonConfig.tenantId }
    };
    this._orgAppInfoHelper.setAppInfo(appDetail);
    TabSdk.store.environmentId = environment.id;

    this.router.navigate([`admin/${Constants.MarketPlace_ScreenID}`]);
  }
}